import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQuery } from "./utils";
import {
  QuestionnaireSectionsForNavigation,
  QuestionGroupWithRelations,
  RestrictedQuestionOptionVisibility,
  QuestionnaireSectionWithStatus,
} from "../../../api/src/types";

export const createQuestionnaireSectionsTagId = (params: {
  userId: string;
  includeStatus: boolean;
}): string => {
  const statusKey = params.includeStatus ? "with-status" : "no-status";
  return `questionnaire-sections:${params.userId}:${statusKey}`;
};

export const createQuestionnaireSectionDetailsTagId = (params: {
  userId: string;
  section: string;
}): string => {
  return `questionnaire-section:${params.userId}:${params.section}`;
};

export const questionnaireApi = createApi({
  reducerPath: "questionnaireApi",
  baseQuery,
  tagTypes: [
    "QuestionnaireSections",
    "QuestionGroup",
    "QuestionOptionVisibility",
  ],
  endpoints: (builder) => ({
    getQuestionnaireSections: builder.query<
      QuestionnaireSectionsForNavigation[],
      { id: string; includeStatus: boolean }
    >({
      query: ({ id, includeStatus }) => ({
        url: `users/${id}/questionnaire-sections?includeStatus=${includeStatus}`,
        method: "GET",
      }),
      providesTags: (_result, _error, args) => [
        {
          type: "QuestionnaireSections" as const,
          id: createQuestionnaireSectionsTagId({
            userId: args.id,
            includeStatus: args.includeStatus,
          }),
        },
      ],
    }),
    getQuestionnaireSectionDetails: builder.query<
      QuestionnaireSectionWithStatus,
      { id: string; section: string }
    >({
      query: ({ id, section }) => ({
        url: `users/${id}/questionnaire-sections/${section}`,
        method: "GET",
      }),
      providesTags: (_result, _error, args) => [
        {
          type: "QuestionnaireSections" as const,
          id: createQuestionnaireSectionDetailsTagId({
            userId: args.id,
            section: args.section,
          }),
        },
      ],
    }),
    getQuestionnaireSectionGroupsByDocumentId: builder.query<
      QuestionGroupWithRelations[],
      { docId: string; section: string; userId: string }
    >({
      query: ({ docId, section }) => ({
        url: `documents/${docId}/questionnaire-sections/${section}/groups`,
        method: "GET",
      }),
      providesTags: [{ type: "QuestionGroup" as const }],
    }),
    getQuestionOptionVisibility: builder.query<
      RestrictedQuestionOptionVisibility,
      { restrictedQuestionOptionIds: string[]; userId: string }
    >({
      query: ({ restrictedQuestionOptionIds, userId }) => ({
        url: `users/${userId}/question-options`,
        method: "POST",
        body: { restrictedQuestionOptionIds },
      }),
      providesTags: ["QuestionOptionVisibility"],
    }),
  }),
});

export const {
  useGetQuestionnaireSectionsQuery,
  useGetQuestionnaireSectionDetailsQuery,
  useGetQuestionnaireSectionGroupsByDocumentIdQuery,
  useGetQuestionOptionVisibilityQuery,
} = questionnaireApi;
